import { z } from 'zod';
import { optionalMongoIdString } from './mongo-id.schema.js';
import { mongoObjectIdString } from './object-id-helpers.js';

/** Empty strings from single-select forms break Mongoose ObjectId casts — omit them before persist. */
function stripEmptyExerciseOptionalFields(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
    const out = { ...data };
    const optionalKeys = [
        'location', 'scope', 'objectives', 'duration', 'outcome', 'reportUrl',
        'code', 'owner', 'status', 'exercise', 'scenario', 'participants',
    ];
    for (const key of optionalKeys) {
        if (out[key] === '' || out[key] === null) {
            delete out[key];
        }
    }
    return out;
}

/** Form may send a single vendor id (Select) or an array (MultiSelect). */
function normalizeParticipatingVendors(value) {
    if (value === '' || value === null || value === undefined) return undefined;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed ? [trimmed] : undefined;
    }
    if (Array.isArray(value)) {
        const filtered = value.filter((id) => id && id !== '');
        return filtered.length ? filtered : undefined;
    }
    return undefined;
}

const optionalParticipatingVendors = z.preprocess(
    normalizeParticipatingVendors,
    z.array(mongoObjectIdString).optional(),
);

const scenarioDevelopmentEntrySchema = z.object({
    id: z.string().optional(),
    content: z.string().min(1),
    author: z.string().optional(),
    authorUser: z.string().optional(),
    date: z.string().optional(),
    createdAt: z.string().or(z.date()).optional(),
    updatedAt: z.string().or(z.date()).optional()
});

const exerciseRemarkSchema = z.object({
    id: z.string().optional(),
    text: z.string().min(1),
    author: z.string().optional(),
    authorUser: z.string().optional(),
    date: z.string().optional(),
    avatar: z.string().optional(),
    period: z.string().optional(),
    createdAt: z.string().or(z.date()).optional(),
    updatedAt: z.string().or(z.date()).optional()
});

export const ExerciseSchema = z.object({
    id: z.string().optional(),
    name: z.string().min(1),
    type: z.string().optional(), // simulation, tabletop, drill, improvement
    plannedDate: z.string().or(z.date()).optional(),
    location: z.string().optional(),
    scope: z.string().optional(),
    objectives: z.string().optional(),
    scenarioDevelopment: z.array(scenarioDevelopmentEntrySchema).optional(),
    otherRemarks: z.array(exerciseRemarkSchema).optional(),
    relatedBcps: z.array(z.string()).optional(),
    exerciseParticipants: z.array(z.string()).optional(),
    relatedAssessments: z.array(z.string()).optional(),
    scenarioTimelines: z.array(z.string()).optional(),
    /** Legacy: string[] ObjectIds. Current: { activity, step }[] */
    exerciseRecoveryActivities: z
        .array(
            z.union([
                z.string(),
                z.object({ activity: z.string(), step: z.number().min(1) })
            ])
        )
        .optional(),
    exerciseEvaluations: z.array(z.string()).optional(),
    improvementActions: z.array(z.string()).optional(),
    participants: z.array(z.any()).or(z.string()).optional(),
    facilitator: optionalMongoIdString,
    participatingVendors: optionalParticipatingVendors,
    duration: z.string().optional(),
    outcome: z.string().optional(), // success, partial, fail
    reportUrl: z.string().optional(),
    // Improvement related fields
    code: z.string().optional(),
    owner: z.string().optional(),
    startDate: z.string().or(z.date()).optional(),
    endDate: z.string().or(z.date()).optional(),
    progress: z.number().optional(),
    status: z.string().optional(),
    exercise: z.string().optional(),
    results: z.string().optional(),
    score: z.number().optional(),
    objectivesMet: z.array(z.object({
        objective: z.string().optional(),
        met: z.boolean().optional(),
        notes: z.string().optional(),
    })).optional(),
    issuesIdentified: z.array(z.object({
        issue: z.string().optional(),
        severity: z.string().optional(),
        assignee: z.string().optional(),
        dueDate: z.string().or(z.date()).optional(),
        status: z.string().optional(),
    })).optional(),
    documents: z.array(z.object({
        name: z.string().optional(),
        url: z.string().optional(),
        type: z.string().optional(),
    })).optional(),
    customFields: z.record(z.unknown()).optional(),
    createdAt: z.date().optional(),
    updatedAt: z.date().optional(),
});
const BaseCreateExerciseSchema = ExerciseSchema.omit({ id: true, createdAt: true, updatedAt: true });
const BaseUpdateExerciseSchema = ExerciseSchema.partial();

export const CreateExerciseSchema = z.preprocess(stripEmptyExerciseOptionalFields, BaseCreateExerciseSchema);
export const UpdateExerciseSchema = z.preprocess(stripEmptyExerciseOptionalFields, BaseUpdateExerciseSchema);

export const ExerciseFormSchema = z.object({
    id: z.union([z.string(), z.number()]).optional(),
    name: z.string().min(1, "form.validation.nameRequired"),
    type: z.string().min(1, "form.validation.typeRequired"),
    objectives: z.string().optional(),
    plannedDate: z.string().min(1, "form.validation.dateRequired").or(z.date()),
    duration: z.string().optional(),
    location: z.string().optional(),
    facilitator: z.string().optional(),
    participatingVendors: z.array(z.string()).or(z.string()).optional(),
});

export const ExerciseImprovementFormSchema = z.object({
    id: z.union([z.string(), z.number()]).optional(),
    code: z.string().optional(),
    name: z.string().min(1, "form.validation.nameRequired"),
    owner: z.string().min(1, "form.validation.ownerRequired"),
    startDate: z.string().min(1, "form.validation.startDateRequired").or(z.date()),
    endDate: z.string().min(1, "form.validation.endDateRequired").or(z.date()),
    progress: z.coerce.number().min(0).max(100),
    status: z.string().min(1, "form.validation.statusRequired"),
    exercise: z.string().min(1, "form.validation.exerciseRequired"),
});
